import { Button, Card, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useMemo, useState } from "react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { getPublicFileUrl } from "@/utils";

export interface ArticleQuery {
    app: number;
    type: number;
}

export interface ArticleData {
    id: number;
    content?: string;
}

const EDITOR_FILE_HEADER = /^==ids=(.*?)\n/;
const MEDIA_SOURCE_PATTERN = /<(?:img|video|source)\b[^>]*\bsrc=["']([^"']+)["']/gi;

/** 读取 Vue 富文本内容头部记录的历史附件 ID。 */
export function getLianYunArticleStoredFileIds(content?: string): number[] {
    const match = content?.match(EDITOR_FILE_HEADER);
    if (!match) return [];
    return Array.from(
        new Set(
            match[1]
                .split(DEFAULT_VALUE)
                .map(Number)
                .filter(fileId => Number.isInteger(fileId) && fileId > 0)
        )
    );
}

/** 从富文本媒体地址提取 Vue 编辑器保存所需的文件 ID。 */
export function getLianYunArticleFileId(source: string): number | undefined {
    const hashId = source.match(/#(\d+)$/)?.[1];
    const signedPathId = source.match(/\/(\d+)(?:\?[^#]*)?$/)?.[1];
    const fileId = Number(hashId || signedPathId);
    return Number.isInteger(fileId) && fileId > 0 ? fileId : undefined;
}

/** 读取当前富文本中仍被图片、视频或音频标签使用的附件 ID。 */
export function getLianYunArticleActiveFileIds(content?: string): number[] {
    const activeFileIds: number[] = [];
    let match: RegExpExecArray | null;
    while ((match = MEDIA_SOURCE_PATTERN.exec(content || "")) !== null) {
        const fileId = getLianYunArticleFileId(match[1]);
        if (fileId && !activeFileIds.includes(fileId)) activeFileIds.push(fileId);
    }
    MEDIA_SOURCE_PATTERN.lastIndex = 0;
    return activeFileIds;
}

/** 移除 Vue 富文本附件头部，供 React 编辑框回显真实内容。 */
export function getLianYunArticleEditorContent(content?: string): string {
    return (content || "").replace(EDITOR_FILE_HEADER, "");
}

/** 对齐 Vue editorManage.handleContentWidth，约束文章预览中的媒体不超出容器。 */
export function getLianYunArticlePreviewContent(content?: string): string {
    return (content || "")
        .replace(/<img/gi, '<img style="width:100%;height:auto"')
        .replace(/<video/gi, '<video style="width:100%;height:auto"');
}

/** 按 Vue editorManage.setContent 契约为文章内容挂载附件 ID。 */
export function setLianYunArticleEditorContent(content: string, activeFileIds: number[]): string {
    return activeFileIds.length ? `==ids=${activeFileIds.join(DEFAULT_VALUE)}\n${content}` : content;
}

/** 将 Vue 接口返回值统一转换为 React 预览状态，并保留历史附件 ID。 */
export function getLianYunArticleState(result: ArticleData | null | undefined, app: number) {
    const nextArticle = result || { id: app, content: "" };
    return {
        article: { ...nextArticle, content: getLianYunArticleEditorContent(nextArticle.content) },
        lastFileIds: getLianYunArticleStoredFileIds(nextArticle.content)
    };
}

export interface LianYunArticlePageProps {
    articleType: number;
    /** 可选的测试/嵌入权限覆盖；未传入时沿用当前登录用户权限。 */
    permissions?: string[];
    loadArticle?: (query: ArticleQuery) => Promise<ArticleData | null | undefined>;
    updateArticle?: (
        id: number,
        payload: { content: string; activeFileIds: number[]; unActiveFileIds: number[] }
    ) => Promise<unknown>;
}

const tabs = [
    { app: 1, label: "链云砂石小程序" },
    { app: 3, label: "链云砂石PC" },
    { app: 4, label: "链云砂石APP" }
];
const aiArticleTypes = new Set([8, 9]);
const carryingArticleTypes = new Set([5, 10, 11]);

/** 沿用旧 Vue onBeforeMount：船务协议默认查询链云船务小程序，智能助理协议查询 PC。 */
export function getInitialLianYunArticleApp(articleType: number): number {
    if (carryingArticleTypes.has(articleType)) return 2;
    if (aiArticleTypes.has(articleType)) return 3;
    return 1;
}

const articleAppTypes = new Set([1, 2, 3, 4, 5, 6]);

/** 读取旧 Vue 路由的 typeId，非法或缺失时回退文章类型默认终端。 */
export function getLianYunArticleAppFromQuery(articleType: number, typeId?: string | null): number {
    const app = Number(typeId);
    return Number.isInteger(app) && articleAppTypes.has(app) ? app : getInitialLianYunArticleApp(articleType);
}

/** 复用 Vue 文章查询接口。 */
export async function loadLianYunArticle(query: ArticleQuery): Promise<ArticleData | null | undefined> {
    const response = await request.service.get<ArticleData | null>("/dataService/admin/article/find", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 文章更新接口。 */
export async function updateLianYunArticle(
    id: number,
    payload: { content: string; activeFileIds: number[]; unActiveFileIds: number[] }
): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/article/${id}`, payload);
    return response.data;
}

/** 关于链云文章页面，保留 Vue 端切换、预览、编辑和保存状态机。 */
export function LianYunArticlePage({
    articleType,
    permissions,
    loadArticle = loadLianYunArticle,
    updateArticle = updateLianYunArticle
}: LianYunArticlePageProps) {
    const storeBanButton = useUserStore(state => state.banButton);
    const writeActionsDisabled = permissions ? permissions.includes("ROLE_BUTTON_BAN") : storeBanButton;
    const availableTabs = useMemo(() => (aiArticleTypes.has(articleType) ? [tabs[1]] : tabs), [articleType]);
    const [app, setApp] = useState(() => {
        const typeId = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("typeId");
        return getLianYunArticleAppFromQuery(articleType, typeId);
    });
    const [article, setArticle] = useState<ArticleData>({ id: app, content: "" });
    const [lastFileIds, setLastFileIds] = useState<number[]>([]);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isWeb = app === 3;

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        void loadArticle({ app, type: articleType })
            .then(result => {
                if (disposed) return;
                const nextState = getLianYunArticleState(result, app);
                setLastFileIds(nextState.lastFileIds);
                setArticle(nextState.article);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setErrorMessage("文章内容加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [app, articleType, loadArticle]);

    const selectTab = (nextApp: number) => {
        setEditing(false);
        setApp(nextApp);
    };

    /** 对齐 Vue onBack：取消编辑时重新读取服务端内容，丢弃未保存修改。 */
    const backToArticle = () => {
        setEditing(false);
        void loadArticle({ app, type: articleType })
            .then(result => {
                const nextState = getLianYunArticleState(result, app);
                setLastFileIds(nextState.lastFileIds);
                setArticle(nextState.article);
                setErrorMessage(null);
            })
            .catch(() => setErrorMessage("文章内容加载失败"));
    };

    const save = async () => {
        if (!article.content?.trim()) {
            setErrorMessage("文章内容不能为空");
            return;
        }
        try {
            const activeFileIds = getLianYunArticleActiveFileIds(article.content);
            const unActiveFileIds = lastFileIds.filter(fileId => !activeFileIds.includes(fileId));
            await updateArticle(article.id, {
                content: setLianYunArticleEditorContent(article.content || "", activeFileIds),
                activeFileIds,
                unActiveFileIds
            });
            setEditing(false);
            setErrorMessage(null);
            // Vue 保存成功后会重新查询，确保编辑后的文章和服务端实际内容一致。
            const refreshedArticle = await loadArticle({ app, type: articleType });
            const nextState = getLianYunArticleState(refreshedArticle, app);
            setLastFileIds(nextState.lastFileIds);
            setArticle(nextState.article);
        } catch {
            setErrorMessage("文章内容保存失败");
        }
    };

    return (
        <section aria-label="lian-yun-article-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <Typography.Title className="mb-4 mt-0" level={4}>
                    关于链云文章
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Space className="mb-4" wrap>
                    {availableTabs.map(tab => (
                        <Button
                            key={tab.app}
                            type={tab.app === app ? "primary" : "default"}
                            onClick={() => selectTab(tab.app)}
                        >
                            {tab.label}
                        </Button>
                    ))}
                </Space>
                <div className="flex min-h-0 flex-1 gap-8">
                    <div className="flex min-w-0 flex-1 flex-col">
                        <Typography.Text className="mb-2">内容</Typography.Text>
                        {loading ? <div>加载中</div> : null}
                        {editing ? (
                            <textarea
                                aria-label="文章内容"
                                className="min-h-96 w-full"
                                value={article.content || ""}
                                onChange={event => setArticle(current => ({ ...current, content: event.target.value }))}
                            />
                        ) : (
                            <div
                                aria-label="文章内容预览"
                                className="min-h-96 whitespace-pre-wrap"
                                dangerouslySetInnerHTML={{
                                    __html: getLianYunArticlePreviewContent(article.content || DEFAULT_VALUE)
                                }}
                            />
                        )}
                    </div>
                    {!isWeb ? (
                        <aside
                            aria-label="手机预览"
                            className="relative h-[735px] w-[350px] shrink-0 origin-top-left scale-[0.84]"
                        >
                            <img
                                alt="链云文章手机预览外框"
                                className="pointer-events-none absolute left-0 top-0 z-10 h-auto w-[369px]"
                                src={getPublicFileUrl("admin/iPhone-border.png")}
                            />
                            <div className="absolute left-[21px] top-[70px] h-[640px] w-[315px] overflow-y-auto bg-[#f5f6fa] break-words">
                                <div
                                    aria-label="手机文章内容"
                                    className="mx-3 my-2 min-h-full overflow-hidden rounded-xl bg-white px-4 py-2"
                                    dangerouslySetInnerHTML={{
                                        __html: getLianYunArticlePreviewContent(article.content || DEFAULT_VALUE)
                                    }}
                                />
                            </div>
                        </aside>
                    ) : null}
                </div>
                <Space className="mt-4">
                    {editing ? (
                        <>
                            <Button
                                aria-label="保存文章"
                                disabled={writeActionsDisabled}
                                type="primary"
                                onClick={() => void save()}
                            >
                                保存
                            </Button>
                            <Button aria-label="返回文章" onClick={backToArticle}>
                                返回
                            </Button>
                        </>
                    ) : (
                        <Button
                            aria-label="修改文章"
                            disabled={writeActionsDisabled}
                            type="primary"
                            onClick={() => setEditing(true)}
                        >
                            修改
                        </Button>
                    )}
                </Space>
            </Card>
        </section>
    );
}
